// LB-342 — B2, B3, B4: what an invited adult sees on their own device.
//
// Three steps on one screen, because they are one decision: this is the household, here is
// your PIN, this phone is yours. The person arriving here has no device in this household,
// no passcode and no household code — the emailed link did all of that — so nothing on this
// screen asks them to prove anything they have not already proved by opening their own email.
import { t as ct } from "../lib/content.js";
import { InstallButton, useInstallPrompt } from "./Onboarding.jsx";

const { useState, useEffect } = React;

// Defaults so Content Studio can render and edit this screen: it has no route into the app
// (it exists only for somebody holding an emailed token), so the CMS renderer mounts it with
// nothing, and without these it would paint the spinner and none of its 24 strings would be
// editable. The real caller always passes both.
export function AcceptInvite({ stage = "confirm", preview = null, onAccept, onGiveUp, adultInvite = null }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  // household -> install -> pin -> device. An install carry can name the step to resume at,
  // because the person crossed into a fresh storage jar mid-flow and should not be asked to
  // agree to the same thing twice. Only "pin" is honoured; anything else starts at the top.
  const [step, setStep] = useState(() => (adultInvite?.step === "pin" ? "pin" : "household"));
  const [busy, setBusy] = useState(false);
  // Read HERE, not inside InstallStep, and unconditionally — every early return below comes
  // after it, so the hook order is fixed. `installable` is false until beforeinstallprompt
  // fires, and that listener is registered in an effect, so a component that decides on its
  // FIRST render decides before the answer exists. Deciding when Join is tapped reads it at a
  // moment the person has already interacted with the page, by which time the event has
  // landed if it is coming at all.
  const { installable } = useInstallPrompt();

  if (stage === "loading") {
    return (
      <Shell>
        <p className="text-sm text-slate-500 text-center">{ct("adults.accept.loading", "Checking your invite…")}</p>
      </Shell>
    );
  }

  // A dead link is answered plainly, with what to do next — never "something went wrong".
  if (stage === "gone") {
    return (
      <Shell>
        <h1 className="text-lg font-bold text-slate-800 mb-1">{ct("adults.accept.gone_title", "That invite has run out")}</h1>
        <p className="text-sm text-slate-500 mb-4">{ct("adults.accept.gone_body", "It may have been used already, or the 24 hours are up. Ask whoever invited you to send another.")}</p>
        <button className="w-full bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5" onClick={onGiveUp} data-testid="invite-gone-out">{ct("adults.accept.gone_out", "Close")}</button>
      </Shell>
    );
  }

  const name = preview?.name || "";
  const household = preview?.householdName || "";
  const kids = preview?.childCount || 0;

  // B2. The household is ALWAYS named, because a person can be in several — and the children
  // are counted, never named (Alan, 18 Sep): the reader is not a member yet.
  if (step === "household") {
    return (
      <Shell>
        <h1 className="text-lg font-bold text-slate-800 mb-1" data-testid="invite-household">
          {ct("adults.accept.join_title", "Join {household}'s household", { household })}
        </h1>
        <p className="text-sm text-slate-500 mb-1">
          {ct("adults.accept.joining_as", "You're joining as {name}.", { name })}
        </p>
        <p className="text-sm text-slate-500 mb-4" data-testid="invite-childcount">
          {kids === 1
            ? ct("adults.accept.children_one", "There is 1 child in this household.")
            : ct("adults.accept.children_many", "There are {count} children in this household.", { count: kids })}
        </p>
        <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2" onClick={() => setStep(installable ? "install" : "pin")} data-testid="invite-join">{ct("adults.accept.join_cta", "Join")}</button>
        <button className="w-full text-xs font-bold text-slate-500 py-1.5" onClick={onGiveUp} data-testid="invite-not-me">{ct("adults.accept.not_me", "Not {name}? Close this", { name })}</button>
      </Shell>
    );
  }

  // I1 — install BEFORE the PIN, and only here.
  //
  // A Home-Screen launch gets its own storage jar, separate from the browser tab it was
  // installed from. So a PIN set in the tab, and the device identity that goes with it, do
  // not follow the icon: Alan installed straight after accepting and the app opened on the
  // join screen, then asked for the OWNER's PIN, because as far as it was concerned it was a
  // different device that had never met him (19 Sep).
  //
  // Installing at this point means nothing has been created yet. The invite link is the only
  // state, it rides across in the URL, and the installed app does the accepting — so the PIN
  // is set once, in the app that keeps it. InstallStep renders nothing at all when install is
  // not on offer (already standalone, or a browser that cannot), and skips straight on.
  if (step === "install") {
    return (
      <InstallStep
        name={name}
        /* Come back at the PIN: the household screen has been seen and Join already tapped. */
        adultInvite={adultInvite ? { ...adultInvite, step: "pin" } : null}
        onSkip={() => setStep("pin")}
      />
    );
  }

  // B3.
  if (step === "pin") {
    return (
      <Shell>
        <h1 className="text-lg font-bold text-slate-800 mb-1">{ct("adults.accept.pin_title", "Create your parent PIN")}</h1>
        <p className="text-sm text-slate-500 mb-4">{ct("adults.accept.pin_body", "This 6-digit PIN keeps Parent Mode for grown-ups only. It is yours rather than the household's, so keep it to yourself; you can change it any time in Settings.")}</p>
        <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="inv-pin">{ct("adults.accept.pin_label", "Set your PIN")}</label>
        <input id="inv-pin" inputMode="numeric" type="password" maxLength={6} className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="invite-pin" />
        <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="inv-pin2">{ct("adults.accept.pin_confirm", "Confirm PIN")}</label>
        <input id="inv-pin2" inputMode="numeric" type="password" maxLength={6} className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2" value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} data-testid="invite-pin-confirm" />
        {error && <p className="text-xs text-rose-600 font-semibold mb-2" data-testid="invite-pin-error">{error}</p>}
        <button
          className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5"
          data-testid="invite-pin-continue"
          onClick={() => {
            if (!/^\d{6}$/.test(pin)) { setError(ct("adults.accept.pin_err_length", "A PIN is exactly 6 digits.")); return; }
            // The starter PIN is the one six-digit string that is not a PIN. The server
            // refuses it too — this is here so the answer arrives before B4, not after.
            if (pin === "123456") { setError(ct("adults.accept.pin_err_starter", "Choose a PIN other than the starter 123456.")); return; }
            if (pin !== confirm) { setError(ct("adults.accept.pin_err_match", "Those two PINs don't match.")); return; }
            setError(""); setStep("device");
          }}
        >{ct("adults.accept.pin_cta", "Continue")}</button>
        <p className="text-xs text-slate-400 font-medium mt-3">{ct("adults.accept.pin_forgot", "If you forget it, we'll send a reset link to {email}.", { email: preview?.email || "" })}</p>
      </Shell>
    );
  }

  // B4, as redrawn 18 Sep: a statement, not a question. No child option — an adult accepting
  // an invite on a phone is not setting up a child's device.
  async function finish(ownDevice) {
    setBusy(true);
    const res = await onAccept(pin, ownDevice);
    setBusy(false);
    if (res && !res.ok) setError(res.error || ct("adults.accept.accept_failed", "Couldn't finish just now. Check your connection and try again"));
  }

  return (
    <Shell>
      <h1 className="text-lg font-bold text-slate-800 mb-1">{ct("adults.accept.device_title", "This phone is yours")}</h1>
      <p className="text-sm text-slate-500 mb-4">{ct("adults.accept.device_body", "You opened your invite here, so we'll treat it as {name}'s device. It opens in Parent Mode and asks for your own PIN.", { name })}</p>
      {error && <p className="text-xs text-rose-600 font-semibold mb-2" data-testid="invite-accept-error">{error}</p>}
      <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2" disabled={busy} onClick={() => finish(true)} data-testid="invite-finish">{ct("adults.accept.device_cta", "Continue")}</button>
      <button className="w-full text-xs font-bold text-slate-500 py-1.5" disabled={busy} onClick={() => finish(false)} data-testid="invite-shared">{ct("adults.accept.device_shared", "This is a shared family device")}</button>
    </Shell>
  );
}

// I1. Kept beside the flow rather than inside it so the "nothing to offer, move on" case is
// one statement: a device that cannot install never sees a screen about installing.
function InstallStep({ name, adultInvite, onSkip }) {
  const { installable } = useInstallPrompt();
  // The caller only routes here when install was on offer, so this is the net for one case:
  // it STOPPED being on offer while the step was up, which is what installing does. Leaving
  // it as the primary decision put the choice on a first render, before
  // beforeinstallprompt had fired — and skipping is a one-way door, so a late event would
  // have found the step already gone. On Android, where the event is the only signal, that
  // is the normal case rather than an edge one.
  useEffect(() => { if (!installable) onSkip(); }, [installable]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!installable) return null;
  return (
    <Shell>
      <h1 className="text-lg font-bold text-slate-800 mb-1" data-testid="invite-install-title">
        {ct("adults.accept.install_title", "{name}, you're in", { name })}
      </h1>
      <p className="text-sm text-slate-500 mb-3">
        {ct("adults.accept.install_body", "Add Little Bankers to your home screen and carry on there. It takes a moment, and you only do it once.")}
      </p>
      <p className="text-xs text-slate-400 font-medium mb-4">
        {ct("adults.accept.install_why", "The installed app keeps its own settings, so anything set up here in the browser wouldn't follow it across.")}
      </p>
      <div className="mb-2">
        <InstallButton variant="wide" adultInvite={adultInvite} label={ct("adults.accept.install_cta", "Add to home screen")} />
      </div>
      <button className="w-full text-xs font-bold text-slate-500 py-1.5" onClick={onSkip} data-testid="invite-install-skip">
        {ct("adults.accept.install_skip", "Carry on in the browser")}
      </button>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center p-6 bg-slate-50" data-testid="accept-invite">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-5">{children}</div>
    </div>
  );
}
